import type { ScenarioDefinition } from "../../domain/types";
import type { ToolCallState } from "../../domain/toolCallMachine";
import { PermissionNotice } from "../PermissionNotice/PermissionNotice";
import { ConfirmationGate } from "../ConfirmationGate/ConfirmationGate";
import { ExecutionControls } from "../ExecutionControls/ExecutionControls";
import "./ToolCallCard.css";

export interface ToolCallCardProps {
  scenario: ScenarioDefinition;
  state: ToolCallState;
  onRequest: () => void;
  onGrantPermission: () => void;
  onDenyPermission: () => void;
  onConfirm: () => void;
  onCancelConfirmation: () => void;
  onCancelRun: () => void;
  onReset: () => void;
}

const STATUS_LABEL: Record<ToolCallState["status"], string> = {
  idle: "En espera",
  "awaiting-permission": "Requiere permiso",
  "permission-denied": "Permiso denegado",
  "awaiting-confirmation": "Requiere confirmación",
  running: "Procesando",
  completed: "Completado",
  error: "Error",
  cancelled: "Cancelado",
};

/**
 * Representa el ciclo completo de una tool call MCP (P3-R1): cabecera con la herramienta,
 * puerta de permiso, puerta de confirmación para acciones sensibles (P3-R2), progreso y
 * control de ejecución/cancelación. El resultado final se muestra en ToolResultPanel.
 */
export function ToolCallCard({
  scenario,
  state,
  onRequest,
  onGrantPermission,
  onDenyPermission,
  onConfirm,
  onCancelConfirmation,
  onCancelRun,
  onReset,
}: ToolCallCardProps) {
  return (
    <article className="tool-call-card" aria-labelledby="tool-call-card-heading">
      <header className="tool-call-card__header">
        <div>
          <h3 id="tool-call-card-heading" className="mono">
            {scenario.toolName}
          </h3>
          <p className="tool-call-card__description">{scenario.description}</p>
        </div>
        <span className="badge badge--info" aria-live="polite">
          {STATUS_LABEL[state.status]}
        </span>
      </header>

      {state.status === "awaiting-permission" && (
        <PermissionNotice
          toolName={scenario.toolName}
          description={`${scenario.title}: esta herramienta necesita permiso explícito antes de ejecutarse.`}
          onGrant={onGrantPermission}
          onDeny={onDenyPermission}
        />
      )}

      <ConfirmationGate
        open={state.status === "awaiting-confirmation"}
        toolName={scenario.toolName}
        consequence="Esta acción es sensible y puede ser difícil de revertir. Revísala antes de continuar."
        confirmPhrase={
          typeof scenario.fixture.payload.confirmPhrase === "string"
            ? scenario.fixture.payload.confirmPhrase
            : undefined
        }
        onConfirm={onConfirm}
        onCancel={onCancelConfirmation}
      />

      <div className="tool-call-card__actions">
        <ExecutionControls
          status={state.status}
          onRun={onRequest}
          onCancel={onCancelRun}
          onReset={onReset}
        />
      </div>
    </article>
  );
}
